import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { Loading } from "../components/Loading";
import { useToast } from "../components/Toast";
import { api } from "../lib/api";
import { keys, useSessionChange } from "../lib/queries";
import { useTitle } from "../lib/useTitle";

const NEXT_KEY = "suivi-siren:after-login";

function rememberNext(path: string | null) {
  try {
    if (path && path.startsWith("/") && !path.startsWith("//")) sessionStorage.setItem(NEXT_KEY, path);
    else sessionStorage.removeItem(NEXT_KEY);
  } catch {
    /* storage unavailable: land on the dashboard */
  }
}

function takeNext(): string {
  try {
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    return next ?? "/tableau-de-bord";
  } catch {
    return "/tableau-de-bord";
  }
}

/** Runs a one-shot POST once, even under React StrictMode's double effects. */
function useOnce(run: () => void) {
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    run();
    // Intentionally mount-only: the token in the URL is consumed a single time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function Login() {
  useTitle("Connexion");
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const login = useMutation({ mutationFn: api.login });

  function submit(e: FormEvent) {
    e.preventDefault();
    rememberNext(params.get("suite"));
    login.mutate(email.trim());
  }

  if (login.isSuccess) {
    return (
      <div className="page page--narrow">
        <div className="centered-state" role="status">
          <h1>Consultez votre boîte mail</h1>
          <p>
            Si <strong>{email}</strong> est une adresse valide, un lien de connexion vient d'y être envoyé. Il
            fonctionne une seule fois pendant 30 minutes.
          </p>
          <div className="actions">
            <button type="button" className="button button--quiet" onClick={() => login.reset()}>
              Utiliser une autre adresse
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page page--narrow">
      <div className="prose">
        <h1>Accéder à votre espace</h1>
        <p>Pas de mot de passe : indiquez votre adresse, nous vous envoyons un lien de connexion.</p>
        <form className="sheet auth-form" onSubmit={submit}>
          <div className="field">
            <label className="field__label" htmlFor="login-email">
              Adresse e-mail
            </label>
            <input
              id="login-email"
              className="input"
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {login.isError && (
            <div className="notice notice--error" role="alert">
              {login.error.message}
            </div>
          )}
          <button type="submit" className="button" disabled={login.isPending}>
            {login.isPending ? "Envoi en cours" : "Recevoir le lien de connexion"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function LoginCallback() {
  useTitle("Connexion");
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const sessionChange = useSessionChange();
  const toast = useToast();
  const verify = useMutation({
    mutationFn: api.verify,
    onSuccess: (me) => {
      sessionChange(me);
      toast(`Connecté en tant que ${me.email}.`);
      navigate(takeNext(), { replace: true });
    },
  });

  useOnce(() => verify.mutate(token));

  if (verify.isError) {
    return (
      <div className="page page--narrow">
        <div className="centered-state">
          <h1>Ce lien ne fonctionne plus</h1>
          <p>{verify.error.message}</p>
          <div className="actions">
            <Link className="button" to="/connexion">
              Recevoir un nouveau lien
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page page--narrow">
      <Loading label="Connexion en cours" />
    </div>
  );
}

export function Confirm() {
  useTitle("Confirmation de l'adresse");
  const { token = "" } = useParams();
  const client = useQueryClient();
  const confirm = useMutation({
    mutationFn: api.confirm,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me }),
  });

  useOnce(() => confirm.mutate(token));

  if (confirm.isError) {
    return (
      <div className="page page--narrow">
        <div className="centered-state">
          <h1>Ce lien de confirmation n'est pas valide</h1>
          <p>{confirm.error.message} Vérifiez que vous avez copié le lien en entier.</p>
          <div className="actions">
            <Link className="button" to="/">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }
  if (!confirm.isSuccess) {
    return (
      <div className="page page--narrow">
        <Loading label="Confirmation en cours" />
      </div>
    );
  }
  return (
    <div className="page page--narrow">
      <div className="centered-state" role="status">
        <h1>Les alertes sont activées</h1>
        <p>
          Votre adresse est confirmée. Vous recevrez un e-mail dès qu'un événement sera publié pour les entreprises
          que vous suivez.
        </p>
        <div className="actions">
          <Link className="button" to="/tableau-de-bord">
            Voir mes entreprises
          </Link>
        </div>
      </div>
    </div>
  );
}

export function Unsubscribe() {
  useTitle("Arrêter les alertes");
  const { token = "" } = useParams();
  const info = useQuery({
    queryKey: ["unsubscribe", token],
    queryFn: () => api.unsubscribeInfo(token),
    retry: false,
  });
  const stop = useMutation({ mutationFn: () => api.unsubscribe(token) });

  if (info.isPending) {
    return (
      <div className="page page--narrow">
        <Loading />
      </div>
    );
  }
  if (info.isError) {
    return (
      <div className="page page--narrow">
        <div className="centered-state">
          <h1>Ce lien de désinscription n'est pas valide</h1>
          <p>{info.error.message} Connectez-vous pour gérer vos entreprises.</p>
          <div className="actions">
            <Link className="button" to="/connexion">
              Se connecter
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const name = info.data.name ?? `le SIREN ${info.data.siren}`;
  if (stop.isSuccess || !info.data.active) {
    return (
      <div className="page page--narrow">
        <div className="centered-state" role="status">
          <h1>Vous ne suivez plus {name}</h1>
          <p>Vous ne recevrez plus d'alerte pour cette entreprise. Vos autres suivis restent actifs.</p>
          <div className="actions">
            <Link className="button button--quiet" to="/connexion?suite=/tableau-de-bord">
              Gérer mes autres entreprises
            </Link>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="page page--narrow">
      <div className="centered-state">
        <h1>Arrêter les alertes pour {name} ?</h1>
        <p>Vous pourrez reprendre le suivi à tout moment depuis l'accueil.</p>
        {stop.isError && (
          <div className="notice notice--error" role="alert">
            {stop.error.message}
          </div>
        )}
        <div className="actions">
          <button type="button" className="button" disabled={stop.isPending} onClick={() => stop.mutate()}>
            Arrêter les alertes
          </button>
          <Link className="button button--quiet" to="/">
            Garder le suivi
          </Link>
        </div>
      </div>
    </div>
  );
}
